/**
 * CtaModal — öffnet ein EXT:form-Formular im barrierefreien <dialog>-Modal,
 * statt zur /kontakt-Seite zu navigieren.
 *
 * Trigger: jedes <a data-modal-form="1:/form_definitions/….form.yaml"> (am CTA
 * gesetzt, wenn der Redakteur im Backend ein Formular gewählt hat). Leeres oder
 * fehlendes Attribut → normaler Link (Progressive Enhancement, kein JS-Zwang).
 *
 * Render/Submit laufen über den typeNum-1750-Endpoint (page.formmodal.typoscript):
 *   GET  {pfad}?type=1750&formId=<id>   → Formular-HTML
 *   POST {pfad}?type=1750&formId=<id>   → Validierung/Finisher/Confirmation
 * Die Server-Antwort ersetzt immer den Modal-Body — so funktionieren Validierung,
 * Multistep und Confirmation nativ (EXT:form-FormRuntime).
 *
 * Tracking: modal_form_open beim Öffnen; generate_lead feuert über die bestehende
 * data-lead-event-Mechanik (fireLeadEvents) sobald die Confirmation im Modal landet.
 */
import { fireLeadEvents } from './Tracking.js';

const ENDPOINT_TYPE = '1750';

function dl() {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function endpointUrl(formId) {
  return `${window.location.pathname}?type=${ENDPOINT_TYPE}&formId=${encodeURIComponent(formId)}`;
}

let dialog = null;
let bodyEl = null;
let lastTrigger = null;

function ensureDialog() {
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.className = 'cta-modal';
  dialog.setAttribute('aria-label', 'Formular'); // dynamisch aus der Formular-Überschrift gesetzt
  // Kein eigener Modal-Titel: das Formular bringt seine eigene Überschrift mit
  // (sonst doppelter Titel). Schließen-Button bleibt als „Notausgang".
  dialog.innerHTML = `
    <div class="cta-modal__inner">
      <button type="button" class="cta-modal__close" aria-label="Schließen" data-modal-close>&times;</button>
      <div class="cta-modal__body" data-modal-body aria-live="polite"></div>
    </div>`;
  document.body.appendChild(dialog);
  bodyEl = dialog.querySelector('[data-modal-body]');

  // Close: × button + backdrop click. ESC is handled natively by <dialog>.
  dialog.addEventListener('click', (e) => {
    if (e.target.closest('[data-modal-close]') || e.target === dialog) {
      closeModal();
    }
  });
  dialog.addEventListener('close', () => {
    bodyEl.innerHTML = '';
    if (lastTrigger) {
      lastTrigger.focus();
      lastTrigger = null;
    }
    document.body.classList.remove('cta-modal-open');
  });

  // Intercept form submit → POST via fetch, re-inject response.
  dialog.addEventListener('submit', (e) => {
    const form = e.target.closest('form');
    if (!form || !bodyEl.contains(form)) return;
    e.preventDefault();
    submitForm(form, e.submitter);
  });

  return dialog;
}

function spinner() {
  bodyEl.innerHTML = '<div class="cta-modal__spinner" role="status" aria-label="Lädt…"></div>';
}

function focusFirst() {
  const target = bodyEl.querySelector('input:not([type=hidden]), textarea, select, button');
  if (target) target.focus({ preventScroll: true }); // kein Page-Scroll beim Öffnen
}

// Accessible name des Dialogs aus der Überschrift des geladenen Formulars setzen.
function syncTitle() {
  const h = bodyEl.querySelector('h1, h2, h3');
  if (h && h.textContent.trim()) dialog.setAttribute('aria-label', h.textContent.trim());
}

async function loadForm(formId) {
  spinner();
  try {
    const res = await fetch(endpointUrl(formId), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    bodyEl.innerHTML = await res.text();
    syncTitle();
    focusFirst();
  } catch {
    bodyEl.innerHTML = '<p class="cta-modal__error">Formular konnte nicht geladen werden. Bitte später erneut versuchen.</p>';
  }
}

async function submitForm(form, submitter) {
  const formId = dialog.dataset.formId;
  // Submit to the form's own action (carries the extbase action=perform + cHash for
  // processing) but augmented with the modal context (type=1750 + formId), which the
  // form's UriBuilder drops. Append RAW (string) — do NOT rebuild via URL/URLSearchParams,
  // that re-encodes the bracketed plugin params and breaks the cHash. type + formId are
  // cHash-excluded, so appending them keeps the existing cHash valid.
  // Fragment (#anchor) abschneiden — sonst landen die angehängten Query-Params
  // im Fragment und erreichen den Server nicht (type=1750/formId gingen verloren).
  const base = (form.getAttribute('action') || window.location.pathname).split('#')[0];
  const sep = base.includes('?') ? '&' : '?';
  const action = `${base}${sep}type=${ENDPOINT_TYPE}&formId=${encodeURIComponent(formId)}`;
  // FormData MIT submitter — sonst fehlt der Submit-Button-Eintrag (z. B.
  // [__currentPage]), den EXT:form zum Erkennen des Schritts braucht → sonst
  // wird nur Seite 1 neu gerendert statt verarbeitet.
  const body = new FormData(form, submitter);
  if (!submitter) {
    const btn = form.querySelector('button[type=submit], input[type=submit]');
    if (btn && btn.name && !body.has(btn.name)) body.append(btn.name, btn.value || '');
  }
  spinner();
  try {
    const res = await fetch(action, {
      method: 'POST',
      body, // kein Content-Type setzen → multipart bleibt korrekt
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    // RedirectFinisher: fetch folgte dem 303 → echte Browser-Navigation auf die
    // Danke-Seite (echter Pageview = trackbar wie auf der Normalseite).
    if (res.redirected && res.url) {
      window.location.assign(res.url);
      return;
    }
    bodyEl.innerHTML = await res.text();
    syncTitle();
    // Confirmation-/Lead-Marker im nachgeladenen HTML auswerten (GA4 generate_lead)
    // für Formulare ohne Redirect (z. B. Confirmation-Finisher).
    fireLeadEvents(bodyEl);
    focusFirst();
  } catch {
    bodyEl.innerHTML = '<p class="cta-modal__error">Senden fehlgeschlagen. Bitte später erneut versuchen.</p>';
  }
}

function openModal(trigger, formId) {
  ensureDialog();
  lastTrigger = trigger;
  dialog.dataset.formId = formId;
  document.body.classList.add('cta-modal-open');
  dialog.showModal();
  dl().push({ event: 'modal_form_open', form_id: formId });
  loadForm(formId);
}

function closeModal() {
  if (dialog && dialog.open) dialog.close();
}

export function initCtaModal() {
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    const trigger = e.target.closest('a[data-modal-form]');
    if (!trigger) return;
    const formId = trigger.getAttribute('data-modal-form');
    if (!formId) return; // leer → normaler Link
    if (typeof HTMLDialogElement === 'undefined') return; // <dialog> nicht unterstützt → normaler Link
    e.preventDefault();
    openModal(trigger, formId);
  });
}
